import { useEffect, useState } from "react";

/**
 * Installing the site as an app.
 *
 * Chrome and Edge fire `beforeinstallprompt` when a site qualifies, and refuse
 * to install without a user gesture — so the event is captured here and held
 * until someone presses the button. iOS never fires it: Safari installs only
 * through Share → Add to Home Screen, which is why the UI has to be able to
 * explain as well as offer.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Already running as an installed app, on either platform's terms. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari's own flag, which predates the standard and is the only
    // signal there.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function useInstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Without this the browser shows its own mini-infobar and the event is
      // gone; holding it is what lets the button exist where it makes sense.
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!event) return;
    await event.prompt();
    const { outcome } = await event.userChoice;
    // The event is single-use whatever they chose.
    setEvent(null);
    if (outcome === "accepted") setInstalled(true);
  };

  return { canInstall: event != null && !installed, installed, install };
}

/**
 * Register the service worker, in production only.
 *
 * In dev Vite serves modules unhashed and rebuilds constantly, so a worker
 * caching them would serve yesterday's code and take an afternoon to work out
 * why.
 */
export function registerServiceWorker() {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;

  // Whether a worker was already driving this page decides what a change of
  // controller means: a fresh install (nothing to reload) or a new deploy
  // taking over (reload, or the tab keeps running the old build).
  const hadController = navigator.serviceWorker.controller != null;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.error("Service worker registration failed:", err);
    });
  });
}
